import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApplyPlan, PlannedChange, ProposalLineState } from '../vendor-quote-flow.types';
import { changeLabel, lineDiff, simulate, sortedChanges, totals } from '../vendor-quote-flow.apply';

export interface CompareVendorQuoteChangesDialogData {
  plan: ApplyPlan;
  /** The scope's lines as they are now. */
  lines: ProposalLineState[];
}

export interface CompareVendorQuoteChangesDialogResult {
  action: 'apply' | 'back';
  plan: ApplyPlan;
}

/** Compare before you apply (mockup H1–H4). Keep / Remove for unquoted lines is written back into the plan. */
@Component({
  selector: 'app-compare-vendor-quote-changes-dialog',
  templateUrl: './compare-vendor-quote-changes-dialog.component.html',
  styleUrls: ['./compare-vendor-quote-changes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompareVendorQuoteChangesDialogComponent {
  plan: ApplyPlan;
  lines: ProposalLineState[];
  rows: PlannedChange[];
  before: { cost: number; sell: number };
  changeLabel = changeLabel;
  lineDiff = lineDiff;

  constructor(
    private dialogRef: MatDialogRef<CompareVendorQuoteChangesDialogComponent, CompareVendorQuoteChangesDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) data: CompareVendorQuoteChangesDialogData,
    private cd: ChangeDetectorRef,
  ) {
    this.plan = data.plan;
    this.lines = data.lines;
    this.rows = sortedChanges(this.plan);
    this.before = totals(this.lines);
  }

  /** Totals after the plan, following the Keep / Remove choices (H1). */
  get after(): { cost: number; sell: number } {
    return totals(simulate(this.lines, this.plan));
  }

  get costDiff(): number {
    return this.after.cost - this.before.cost;
  }

  get sellDiff(): number {
    return this.after.sell - this.before.sell;
  }

  setRemove(c: PlannedChange, remove: boolean): void {
    c.remove = remove;
    this.cd.markForCheck();
  }

  changed(c: PlannedChange, field: 'qty' | 'unitCost' | 'vendorName'): boolean {
    return !!c.before && !!c.after && c.before[field] !== c.after[field];
  }

  absolute(n: number): number {
    return Math.abs(n);
  }

  renamed(c: PlannedChange): boolean {
    return !!c.before && !!c.after && c.before.name !== c.after.name;
  }

  trackByRow(index: number, c: PlannedChange): string {
    return `${c.kind}:${c.before?.id ?? 'n'}:${c.after?.productId ?? 'n'}`;
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  back(): void {
    this.dialogRef.close({ action: 'back', plan: this.plan });
  }

  apply(): void {
    this.dialogRef.close({ action: 'apply', plan: this.plan });
  }
}
